import { env } from '@/config/env'

const ADDON_PREFIX = 'addon:'

/** Platform-included CP entitlements (not sold as marketplace packages). */
const PLATFORM_INCLUDED_ADDON_CODES = new Set(['accessibility_pack', 'access_control', 'multilingual'])

function celebrationStorageKeys(): { initKey: string; ackKey: string } {
  const base = env.apiUrl
  return {
    initKey: `tb.admin.cpAddonCelebration.init.v1:${base}`,
    ackKey: `tb.admin.cpAddonCelebration.ack.v1:${base}`,
  }
}

/**
 * Purchasable add-on codes derived from `cpEntitlements` (excludes platform-only keys).
 */
export function purchasableAddonCodesFromCpEntitlements(entitlements: string[]): string[] {
  const out: string[] = []
  for (const raw of entitlements) {
    const s = String(raw).trim()
    if (!s.startsWith(ADDON_PREFIX)) {
      continue
    }
    const code = s.slice(ADDON_PREFIX.length)
    if (!code || PLATFORM_INCLUDED_ADDON_CODES.has(code)) {
      continue
    }
    out.push(code)
  }
  return [...new Set(out)].sort()
}

function readAckSet(ackKey: string): Set<string> {
  try {
    const raw = localStorage.getItem(ackKey)
    if (!raw) {
      return new Set()
    }
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return new Set()
    }
    return new Set(parsed.map((x) => String(x).trim()).filter(Boolean))
  } catch {
    return new Set()
  }
}

function writeAckSet(ackKey: string, set: Set<string>): void {
  try {
    localStorage.setItem(ackKey, JSON.stringify([...set].sort()))
  } catch {
    /* ignore quota / private mode */
  }
}

function isCelebrationBaselineDone(initKey: string): boolean {
  return localStorage.getItem(initKey) === '1'
}

function markCelebrationBaselineDone(initKey: string): void {
  try {
    localStorage.setItem(initKey, '1')
  } catch {
    /* ignore */
  }
}

/**
 * After a successful public tenant-branding fetch: returns newly unlocked purchasable codes
 * since the user last acknowledged (or since first baseline snapshot).
 */
export function computePendingCpAddonCelebrationCodes(currentPurchasable: string[]): string[] {
  const { initKey, ackKey } = celebrationStorageKeys()
  const ack = readAckSet(ackKey)

  if (!isCelebrationBaselineDone(initKey)) {
    markCelebrationBaselineDone(initKey)
    for (const c of currentPurchasable) {
      ack.add(c)
    }
    writeAckSet(ackKey, ack)
    return []
  }

  return currentPurchasable.filter((c) => !ack.has(c))
}

/**
 * Persists acknowledgement so the same packages are not celebrated again until new ones appear.
 */
export function acknowledgeCpAddonCodesInStorage(codes: string[]): void {
  if (codes.length === 0) {
    return
  }
  const { ackKey } = celebrationStorageKeys()
  const ack = readAckSet(ackKey)
  for (const c of codes) {
    const t = String(c).trim()
    if (t) {
      ack.add(t)
    }
  }
  writeAckSet(ackKey, ack)
}
